import type { Timeseries } from "../types";
import type { DimensionSeries } from "../config/data";

export type DistributionType = "normal" | "log-normal" | "logit-normal";

export interface DistributionFit {
  /** Q-Q correlation coefficient (PPCC) - 1.0 means perfect fit */
  qqCorrelation: number;
  /** Human-readable interpretation */
  interpretation: "Excellent" | "Good" | "Acceptable" | "Poor";
}

export interface DistributionStats {
  /** Mean statistic timeseries (geometric mean for log-normal, logistic mean for logit-normal, arithmetic mean for normal) */
  meanSeries: Timeseries;
  /** Variance statistic timeseries (log-variance for log-normal, logit-variance for logit-normal, variance for normal) */
  varianceSeries: Timeseries;
  /** How well the data fits the assumed distribution */
  fit: DistributionFit;
}

/**
 * Computes distribution statistics from dimension series data.
 *
 * For log-normal distribution (data in (0, ∞)):
 * - Mean: geometric mean = exp(mean(log(values)))
 * - Variance: log-variance = variance(log(values))
 *
 * For logit-normal distribution (data in (0, 1)):
 * - Mean: logistic mean = logistic(mean(logit(values)))
 * - Variance: logit-variance = variance(logit(values))
 *
 * For normal distribution:
 * - Mean: arithmetic mean
 * - Variance: sample variance
 *
 * @param dimensionData - Dimension series with values per category per timestamp
 * @param distribution - Distribution type ("normal", "log-normal", or "logit-normal")
 * @returns Distribution statistics as timeseries
 */
export function computeDistributionStats(
  dimensionData: DimensionSeries,
  distribution: DistributionType,
): DistributionStats {
  // Collect all unique timestamps across all dimension values
  const timestampSet = new Set<number>();
  for (const series of Object.values(dimensionData)) {
    for (const ts of series.timestamps) {
      timestampSet.add(ts.getTime());
    }
  }

  // Sort timestamps chronologically
  const sortedTimestamps = Array.from(timestampSet).sort((a, b) => a - b);

  // Build lookup maps for each dimension value's data
  const dimensionMaps: Map<number, number | null>[] = Object.values(dimensionData).map(series => {
    const map = new Map<number, number | null>();
    for (let i = 0; i < series.timestamps.length; i++) {
      map.set(series.timestamps[i].getTime(), series.data[i]);
    }
    return map;
  });

  const meanData: (number | null)[] = [];
  const varianceData: (number | null)[] = [];

  for (const tsMs of sortedTimestamps) {
    // Collect all values for this timestamp
    const values: number[] = [];
    for (const dimMap of dimensionMaps) {
      const val = dimMap.get(tsMs);
      if (val != null && isValidForDistribution(val, distribution)) {
        values.push(val);
      }
    }

    if (values.length < 2) {
      // Need at least 2 values for variance
      meanData.push(null);
      varianceData.push(null);
      continue;
    }

    if (distribution === "log-normal") {
      // Log-normal: compute statistics on log-transformed values
      const logValues = values.map(v => Math.log(v));
      const logMean = arithmeticMean(logValues);
      const logVariance = sampleVariance(logValues, logMean);

      // Store geometric mean (exp of log-mean) for display
      meanData.push(Math.exp(logMean));
      varianceData.push(logVariance);
    } else if (distribution === "logit-normal") {
      // Logit-normal: compute statistics on logit-transformed values
      const logitValues = values.map(v => logit(v));
      const logitMean = arithmeticMean(logitValues);
      const logitVariance = sampleVariance(logitValues, logitMean);

      // Store logistic mean (logistic of logit-mean) for display
      meanData.push(logistic(logitMean));
      varianceData.push(logitVariance);
    } else {
      // Normal: compute statistics directly
      const mean = arithmeticMean(values);
      const variance = sampleVariance(values, mean);

      meanData.push(mean);
      varianceData.push(variance);
    }
  }

  const timestamps = sortedTimestamps.map(ms => new Date(ms));

  // Compute distribution fit (Q-Q correlation)
  const allValues = collectAllValues(dimensionData, distribution);
  const valuesToTest =
    distribution === "log-normal"
      ? allValues.map(v => Math.log(v))
      : distribution === "logit-normal"
        ? allValues.map(v => logit(v))
        : allValues;
  const qqCorrelation = computeQQCorrelation(valuesToTest);
  const fit: DistributionFit = {
    qqCorrelation,
    interpretation: interpretQQCorrelation(qqCorrelation),
  };

  return {
    meanSeries: { timestamps, data: meanData },
    varianceSeries: { timestamps, data: varianceData },
    fit,
  };
}

function arithmeticMean(values: number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

/**
 * Logit transform: log(x / (1-x))
 * Maps (0, 1) → (-∞, +∞)
 */
function logit(x: number): number {
  return Math.log(x / (1 - x));
}

/**
 * Logistic (inverse logit) transform: 1 / (1 + exp(-x))
 * Maps (-∞, +∞) → (0, 1)
 */
function logistic(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

/**
 * Check if a value is valid for the given distribution type.
 */
function isValidForDistribution(val: number, distribution: DistributionType): boolean {
  if (distribution === "log-normal") {
    // Log-normal requires positive values
    return val > 0;
  }
  if (distribution === "logit-normal") {
    // Logit-normal requires values in (0, 1) exclusive
    return val > 0 && val < 1;
  }
  // Normal: any finite value
  return isFinite(val);
}

function sampleVariance(values: number[], mean: number): number {
  if (values.length < 2) return 0;
  let sumSquaredDiff = 0;
  for (const v of values) {
    const diff = v - mean;
    sumSquaredDiff += diff * diff;
  }
  // Sample variance (n-1 denominator)
  return sumSquaredDiff / (values.length - 1);
}

/**
 * Compute Q-Q correlation coefficient (PPCC) to assess normality fit.
 * Higher values (closer to 1) indicate better fit to normal distribution.
 */
function computeQQCorrelation(values: number[]): number {
  if (values.length < 3) return 0;

  const n = values.length;
  const sorted = [...values].sort((a, b) => a - b);

  // Compute theoretical normal quantiles using plotting positions
  const theoretical: number[] = [];
  for (let i = 0; i < n; i++) {
    const p = (i + 0.5) / n; // Hazen plotting position
    theoretical.push(normalQuantile(p));
  }

  return pearsonCorrelation(sorted, theoretical);
}

/**
 * Approximate inverse normal CDF (probit function).
 * Uses Abramowitz and Stegun approximation (formula 26.2.23).
 */
function normalQuantile(p: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  if (p === 0.5) return 0;

  // Work with the lower tail
  const sign = p < 0.5 ? -1 : 1;
  const pp = p < 0.5 ? p : 1 - p;

  const t = Math.sqrt(-2 * Math.log(pp));

  // Rational approximation coefficients
  const c0 = 2.515517;
  const c1 = 0.802853;
  const c2 = 0.010328;
  const d1 = 1.432788;
  const d2 = 0.189269;
  const d3 = 0.001308;

  const numerator = c0 + c1 * t + c2 * t * t;
  const denominator = 1 + d1 * t + d2 * t * t + d3 * t * t * t;

  return sign * (t - numerator / denominator);
}

/**
 * Compute Pearson correlation coefficient between two arrays.
 */
function pearsonCorrelation(x: number[], y: number[]): number {
  const n = x.length;
  if (n !== y.length || n < 2) return 0;

  const meanX = arithmeticMean(x);
  const meanY = arithmeticMean(y);

  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (let i = 0; i < n; i++) {
    const dx = x[i] - meanX;
    const dy = y[i] - meanY;
    sumXY += dx * dy;
    sumX2 += dx * dx;
    sumY2 += dy * dy;
  }

  const denominator = Math.sqrt(sumX2 * sumY2);
  if (denominator === 0) return 0;

  return sumXY / denominator;
}

/**
 * Interpret the Q-Q correlation coefficient.
 */
function interpretQQCorrelation(r: number): DistributionFit["interpretation"] {
  if (r >= 0.99) return "Excellent";
  if (r >= 0.97) return "Good";
  if (r >= 0.95) return "Acceptable";
  return "Poor";
}

/**
 * Collect all valid values from dimension series (pooled across all timestamps).
 */
function collectAllValues(dimensionData: DimensionSeries, distribution: DistributionType): number[] {
  const values: number[] = [];
  for (const series of Object.values(dimensionData)) {
    for (const val of series.data) {
      if (val != null && isValidForDistribution(val, distribution)) {
        values.push(val);
      }
    }
  }
  return values;
}
